import { ProductPagination } from "@/components/ProductPage";
import type { TableProps } from "antd";
import { Button, Empty, Skeleton, Table } from "antd";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

type OrgPageHeaderProps = {
  title: string;
  description?: string;
  actions?: ReactNode;
  meta?: ReactNode;
};

type OrgToolbarProps = {
  children: ReactNode;
  className?: string;
};

type OrgListToolbarProps = {
  search?: ReactNode;
  filters?: ReactNode;
  actions?: ReactNode;
  className?: string;
};

type OrgTableCardProps = {
  children: ReactNode;
  className?: string;
};

type OrgFormSectionProps = {
  title: string;
  description?: string;
  children: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  className?: string;
};

type OrgFormActionsProps = {
  children: ReactNode;
  className?: string;
};

type OrgMetricCardProps = {
  label: string;
  value: ReactNode;
  description?: ReactNode;
  tone?: "neutral" | "dark" | "gold";
};

type OrgEmptyStateProps = {
  title: string;
  description?: string;
  action?: ReactNode;
};

type OrgStatusTagProps = {
  children: ReactNode;
  tone?: "neutral" | "active" | "success" | "warning" | "danger";
};

type OrgDataTablePagination = {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
};

type OrgDataTableProps<RecordType extends object> = Omit<
  TableProps<RecordType>,
  "dataSource" | "pagination" | "loading"
> & {
  dataSource: RecordType[];
  loading?: boolean;
  itemLabel: string;
  emptyTitle: string;
  emptyDescription?: string;
  pagination?: OrgDataTablePagination;
  initialPageSize?: number;
  pageSizeOptions?: string[];
};

const metricToneClasses: Record<NonNullable<OrgMetricCardProps["tone"]>, string> = {
  neutral: "border-gray-200 bg-white text-[#131B2A]",
  dark: "border-white/10 bg-[#131B2A] text-white",
  gold: "border-(--brand-primary)/40 bg-(--brand-primary)/10 text-[#131B2A]",
};

const statusToneClasses: Record<NonNullable<OrgStatusTagProps["tone"]>, string> = {
  neutral: "border-gray-200 bg-gray-50 text-gray-600",
  active: "border-(--brand-primary)/40 bg-(--brand-primary)/10 text-[#805A19]",
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  warning: "border-amber-200 bg-amber-50 text-amber-700",
  danger: "border-red-200 bg-red-50 text-red-700",
};

export const orgInteractiveTableRowClassName =
  "cursor-pointer outline-none [&>td]:transition-colors [&:hover>td]:!bg-[#FFFCF7] [&:active>td]:!bg-[#FFF8EC] focus-visible:[&>td]:!bg-[#FFF8EC]";

/**
 * Renders the standard compact header for organisation settings pages.
 */
export const OrgPageHeader = ({ title, description, actions, meta }: OrgPageHeaderProps) => (
  <header className="flex flex-col gap-3 border-b border-gray-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
    <div className="min-w-0">
      <div className="mb-2 h-0.5 w-7 rounded-full bg-(--brand-primary)" />
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <h1 className="truncate text-[22px] font-semibold leading-tight tracking-tight text-[#131B2A]">{title}</h1>
        {meta}
      </div>
      {description ? <p className="mt-1.5 max-w-3xl text-sm leading-6 text-gray-500">{description}</p> : null}
    </div>
    {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
  </header>
);

/**
 * Renders a consistent filter and action bar for org list pages.
 */
export const OrgToolbar = ({ children, className = "" }: OrgToolbarProps) => (
  <section
    className={`rounded-xl border border-gray-200 bg-white p-2.5 shadow-[0_1px_2px_rgba(19,27,42,0.04)] ${className}`}
  >
    <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">{children}</div>
  </section>
);

/**
 * Renders the standard search, filter, and utility action layout for organisation lists.
 */
export const OrgListToolbar = ({ search, filters, actions, className = "" }: OrgListToolbarProps) => (
  <section
    aria-label="List controls"
    className={`rounded-xl border border-gray-200 bg-white p-2.5 shadow-[0_1px_2px_rgba(19,27,42,0.04)] ${className}`}
  >
    <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
      {search ? <div className="min-w-0 flex-1">{search}</div> : null}
      {filters ? <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">{filters}</div> : null}
      {actions ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2 xl:border-l xl:border-t-0 xl:pl-2 xl:pt-0">
          {actions}
        </div>
      ) : null}
    </div>
  </section>
);

/**
 * Wraps dense organisation tables with structural product styling.
 */
export const OrgTableCard = ({ children, className = "" }: OrgTableCardProps) => (
  <section
    className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)] ${className}`}
  >
    {children}
  </section>
);

/**
 * Groups related settings fields into a titled, structurally separated form surface.
 */
export const OrgFormSection = ({
  title,
  description,
  children,
  actions,
  footer,
  className = "",
}: OrgFormSectionProps) => (
  <section
    className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)] ${className}`}
  >
    <header className="flex flex-col gap-3 border-b border-gray-200 bg-[#FBFBFB] px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <div>
        <h2 className="text-sm font-semibold text-[#131B2A]">{title}</h2>
        {description ? <p className="mt-1 text-xs leading-5 text-gray-500">{description}</p> : null}
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </header>
    <div className="p-4 sm:p-5">{children}</div>
    {footer ? (
      <footer className="flex items-center justify-end border-t border-gray-200 bg-[#FBFBFB] px-4 py-3.5 sm:px-5">
        {footer}
      </footer>
    ) : null}
  </section>
);

/**
 * Renders consistent form actions with a clear boundary from editable fields.
 */
export const OrgFormActions = ({ children, className = "" }: OrgFormActionsProps) => (
  <footer
    className={`flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end ${className}`}
  >
    {children}
  </footer>
);

/**
 * Displays a structured loading skeleton matching organisation settings forms.
 */
export const OrgFormPageSkeleton = () => (
  <div className="space-y-4" aria-label="Loading settings">
    <div className="space-y-2 border-b border-gray-200 pb-5">
      <Skeleton.Input active size="small" className="!w-44" />
      <Skeleton.Input active size="small" className="!w-full !max-w-xl" />
    </div>
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_280px]">
      <OrgTableCard className="p-5">
        <Skeleton active paragraph={{ rows: 7 }} />
      </OrgTableCard>
      <OrgTableCard className="h-fit p-5">
        <Skeleton active paragraph={{ rows: 3 }} />
      </OrgTableCard>
    </div>
  </div>
);

/**
 * Displays a small organisation metric or state summary.
 */
export const OrgMetricCard = ({ label, value, description, tone = "neutral" }: OrgMetricCardProps) => (
  <div
    className={`relative overflow-hidden rounded-xl border px-4 py-3.5 shadow-[0_1px_2px_rgba(19,27,42,0.035)] ${metricToneClasses[tone]}`}
  >
    {tone === "gold" ? <div className="absolute inset-x-0 top-0 h-0.5 bg-(--brand-primary)" /> : null}
    <p className={`text-xs font-medium ${tone === "dark" ? "text-white/55" : "text-[#131B2A]/45"}`}>{label}</p>
    <div className="mt-1.5 text-xl font-semibold tracking-tight tabular-nums">{value}</div>
    {description ? (
      <p className={`mt-1 text-xs leading-5 ${tone === "dark" ? "text-white/55" : "text-gray-500"}`}>{description}</p>
    ) : null}
  </div>
);

/**
 * Displays compact metadata or workflow status using the organisation color system.
 */
export const OrgStatusTag = ({ children, tone = "neutral" }: OrgStatusTagProps) => (
  <span
    className={`inline-flex min-h-6 items-center rounded-md border px-2 py-0.5 text-xs font-semibold leading-4 ${statusToneClasses[tone]}`}
  >
    {children}
  </span>
);

/**
 * Renders a compact empty state for org tables and setup flows.
 */
export const OrgEmptyState = ({ title, description, action }: OrgEmptyStateProps) => (
  <div className="rounded-xl border border-dashed border-gray-300 bg-[#FBFBFB] px-6 py-10 text-center">
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={null} />
    <h3 className="mt-2 text-sm font-semibold text-[#131B2A]">{title}</h3>
    {description ? <p className="mx-auto mt-1 max-w-md text-sm leading-6 text-gray-500">{description}</p> : null}
    {action ? <div className="mt-4">{action}</div> : null}
  </div>
);

/**
 * Renders a responsive organisation table with one consistent pagination footer.
 */
export const OrgDataTable = <RecordType extends object>({
  dataSource,
  loading = false,
  itemLabel,
  emptyTitle,
  emptyDescription,
  pagination,
  initialPageSize = 10,
  pageSizeOptions = ["10", "20", "50", "100"],
  onChange,
  scroll,
  ...tableProps
}: OrgDataTableProps<RecordType>) => {
  const [clientPage, setClientPage] = useState(1);
  const [clientPageSize, setClientPageSize] = useState(initialPageSize);
  const isServerPaginated = Boolean(pagination);
  const current = pagination?.current ?? clientPage;
  const pageSize = pagination?.pageSize ?? clientPageSize;
  const total = pagination?.total ?? dataSource.length;
  const safeCurrent = Math.max(1, Math.min(current, Math.max(1, Math.ceil(total / pageSize))));

  useEffect(() => {
    if (!isServerPaginated) setClientPage(1);
  }, [dataSource, isServerPaginated]);

  if (loading && dataSource.length === 0) {
    return (
      <OrgTableCard>
        <div className="space-y-3 p-4" aria-label={`Loading ${itemLabel}`}>
          <Skeleton.Input active block size="small" />
          {Array.from({ length: Math.min(initialPageSize, 8) }, (_, index) => (
            <Skeleton.Input key={index} active block size="large" />
          ))}
        </div>
      </OrgTableCard>
    );
  }

  return (
    <OrgTableCard className={loading ? "opacity-70 transition-opacity" : "transition-opacity"}>
      <div className="overflow-hidden p-2 pb-0" aria-busy={loading}>
        <Table<RecordType>
          {...tableProps}
          dataSource={dataSource}
          scroll={{ x: "max-content", ...scroll }}
          pagination={{
            current: safeCurrent,
            pageSize,
            total,
            position: [],
            showSizeChanger: false,
          }}
          locale={{
            emptyText: <OrgEmptyState title={emptyTitle} description={emptyDescription} />,
          }}
          onChange={(nextPagination, filters, sorter, extra) => {
            if (!isServerPaginated && nextPagination.current) setClientPage(nextPagination.current);
            onChange?.(nextPagination, filters, sorter, extra);
          }}
        />
      </div>
      <ProductPagination
        current={safeCurrent}
        pageSize={pageSize}
        total={total}
        itemLabel={itemLabel}
        pageSizeOptions={pageSizeOptions}
        onChange={(nextPage, nextPageSize) => {
          if (pagination) {
            pagination.onChange(nextPage, nextPageSize);
            return;
          }
          setClientPage(nextPage);
        }}
        onPageSizeChange={(nextPageSize) => {
          if (pagination) {
            pagination.onPageSizeChange?.(nextPageSize);
            return;
          }
          setClientPageSize(nextPageSize);
          setClientPage(1);
        }}
      />
    </OrgTableCard>
  );
};

/**
 * Returns a navy primary button class for Ant Design buttons.
 */
export const orgPrimaryButtonClass =
  "!h-10 !rounded-lg !border-(--brand-secondary) !bg-(--brand-secondary) !px-4 !font-semibold !text-[var(--brand-secondary-foreground)] hover:!border-(--brand-secondary-hover) hover:!bg-(--brand-secondary-hover) active:!scale-[0.98]";

/**
 * Convenience button used in empty states when an action is required.
 */
export const OrgEmptyActionButton = ({ children, onClick }: { children: ReactNode; onClick?: () => void }) => (
  <Button type="primary" onClick={onClick} className={orgPrimaryButtonClass}>
    {children}
  </Button>
);
